import { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Switch,
  FormControlLabel,
  Avatar,
} from "@mui/material";
import {
  CheckCircle,
  Edit,
  Search,
  CalendarToday,
  Assignment,
  Business,
  Notes,
  Add,
  Close,
  Chat,
  CheckBox,
  CheckBoxOutlineBlank,
  Numbers,
} from "@mui/icons-material";

import HttpClient from "../../../../helpers/HttpClient";
import { showToast } from "../../../../utils/toast";
import OverlayLoader from "../../../../components/OverlayLoader";

import NumericInput from "../../Components/NumericInput";
import CourseManagerDialog from "../../Components/CourseManagerDialog";
import PinAutocomplete, {
  PinAutocompleteRef,
} from "../../Components/PinAutocomplete";
import { ToggleWithDescription } from "../../Components/Toogle";

interface SerialData {
  serial: string;
  lote: string;
  fecha: string;
  estado: string;
  meses: number;
  cursos: { code: string; name: string; chatEnabled: boolean }[];
  distribuidor: { id: number; name: string };
  observaciones: string;
}

export default function SerialManagementPage() {
  const [serialData, setSerialData] = useState<SerialData>(null);
  const pinRef = useRef<PinAutocompleteRef>(null);
  const [searchSerial, setSearchSerial] = useState("");
  const [openMesesDialog, setOpenMesesDialog] = useState(false);
  const [openCursosDialog, setOpenCursosDialog] = useState(false);
  const [newMeses, setNewMeses] = useState("12");
  const [selectedCursos, setSelectedCursos] = useState([]);
  const [openLoader, setOpenLoader] = useState(true);
  const [cursosDisponibles, setCursosDisponibles] = useState([]);

  const handleCloseCursosDialog = () => {
    setOpenCursosDialog(false);
  };

  const getCourses = async () => {
    const result = await HttpClient().get(`/admin/courses`);
    if (result.error) {
      setOpenLoader(false);
      showToast(
        "No se pudo cargar información de cursos. Refresca la página e intenta nuevamente.",
        "error"
      );
      return;
    }

    setCursosDisponibles(result.data);
    setOpenLoader(false);
  };

  const handleChangeMeses = () => {
    setSerialData((prev) => ({ ...prev, meses: newMeses }));
    setOpenMesesDialog(false);
  };

  const handleChangeCursos = () => {
    setSerialData((prev) => ({ ...prev, cursos: selectedCursos }));
    setOpenCursosDialog(false);
  };

  const handleToggleChat = (cursoCode: string) => {
    setSerialData((prev) => ({
      ...prev,
      cursos: prev.cursos.map((curso) =>
        curso.code === cursoCode
          ? { ...curso, chatEnabled: !curso.chatEnabled }
          : curso
      ),
    }));
  };

  const handleToggleChatMasivo = (enabled: boolean) => {
    setSerialData((prev) => ({
      ...prev,
      cursos: prev.cursos.map((curso) => ({ ...curso, chatEnabled: enabled })),
    }));
  };

  const handleToggleChatMasivoModal = (enabled: boolean) => {
    setSelectedCursos((prev) =>
      prev.map((curso) => ({ ...curso, chatEnabled: enabled }))
    );
  };

  const handleAddCurso = (curso: { code: string; name: string }) => {
    if (!selectedCursos.find((c) => c.code === curso.code)) {
      setSelectedCursos((prev) => [...prev, { ...curso, chatEnabled: false }]);
    }
  };

  const handleRemoveCurso = (cursoCode: string) => {
    console.log("Eliminando curso:", cursoCode);
    setSelectedCursos((prev) => prev.filter((c) => c.code !== cursoCode));
  };

  const handleToggleChatInModal = (cursoCode: string) => {
    setSelectedCursos((prev) =>
      prev.map((curso) =>
        curso.code === cursoCode
          ? { ...curso, chatEnabled: !curso.chatEnabled }
          : curso
      )
    );
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case "activo":
        return "success";
      case "Usado":
        return "warning";
      case "deshabilitado":
        return "error";
      default:
        return "default";
    }
  };

  const isFormValid = () => {
    const meses = Number.parseInt(serialData.meses) || 0;
    return serialData.cursos.length > 0 && meses >= 1;
  };

  const handlerUpdate = async () => {
    setOpenLoader(true);
    const result = await HttpClient().put(`/admin/pin/edit`, {
      serial: serialData.serial,
      months: serialData.meses,
      history: serialData.observaciones,
      courses: [...new Set(serialData.cursos.map((ele) => ele.code))].join(","),
      selectedCoursesData: serialData.cursos,
      status: serialData.estado == "activo" ? 1 : 0,
    });
    console.log(result);
    if (result.error) {
      setOpenLoader(false);
      showToast(
        "No se pudo actualizar ell serial. Intenta nuevamente.",
        "error"
      );
      return;
    }
    setOpenLoader(false);
    if (result.status == 201) {
      showToast("El serial fue actualizado correctamente.", "success");
      setSerialData(null);
      pinRef.current?.clear();
      setSelectedCursos([])
    } else {
      showToast(
        "No se actualizó el serial. Intenta nuevamente. Si el error persiste contacta al soporte.",
        "warning"
      );
    }
  };

  useEffect(() => {
    getCourses();
  }, []);

  return (
    <Box sx={{ backgroundColor: "#F5F5F5", minHeight: "100vh", p: 2 }}>
      <OverlayLoader open={openLoader} />
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          color="#374151"
          sx={{ fontSize: "1.3rem" }}
        >
          Gestión de seriales online
        </Typography>
      </Box>

      {/* Búsqueda de Serial */}
      <Card
        sx={{
          mb: 2,
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          backgroundColor: "white",
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <PinAutocomplete
              ref={pinRef}
              onPinSelect={(pin) => {
                if (pin) {
                  console.log(pin);
                  setSerialData({
                    serial: pin.pin,
                    lote: pin?.lote_id || "Sin lote",
                    fecha: pin?.pin_fecha_creacion_formatted || "",
                    estado: pin.estado,
                    meses: pin.meses_licencia,
                    cursos: pin.cursos,
                    distribuidor: pin.distribuidor,
                    observaciones: pin.historial || "",
                  });
                } else {
                  setSerialData(null);
                }
              }}
              placeholder="Buscar PIN por código, usuario, email..."
              label="Seleccionar PIN"
              showOnlyActiveToggle={false}
              onlyActive={false}
            />
          </Box>
        </CardContent>
      </Card>

      {serialData && (
        <Card
          sx={{
            borderRadius: 2,
            border: "1px solid #E5E7EB",
            backgroundColor: "white",
          }}
        >
          <CardContent sx={{ p: 2 }}>
            {/* Header con estado */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
            >
              <CheckCircle sx={{ color: "#10B981", fontSize: 20 }} />
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: "0.8rem" }}
              >
                Serial seleccionado del sistema
              </Typography>
            </Box>

            {/* Serial Principal */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ fontSize: "1rem" }}
              >
                Serial:
              </Typography>
              <Chip
                label={serialData.serial}
                sx={{
                  backgroundColor: "#E3F2FD",
                  color: "#1976D2",
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  px: 1.5,
                  py: 0.5,
                }}
              />
            </Box>

            {/* Información Básica en Cards optimizadas */}
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E5E7EB",
                    borderRadius: 1.5,
                    height: 70,
                  }}
                >
                  <CardContent
                    sx={{
                      p: 1.5,
                      textAlign: "center",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <Assignment sx={{ color: "#6B7280", fontSize: 14 }} />
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ fontSize: "0.7rem" }}
                      >
                        Lote:
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      sx={{
                        wordBreak: "break-word",
                        lineHeight: 1.1,
                        fontSize: "0.65rem",
                        color: "#374151",
                        backgroundColor: "white",
                        p: 0.5,
                        borderRadius: 0.5,
                        border: "1px solid #E5E7EB",
                        textAlign: "center",
                      }}
                    >
                      {serialData.lote}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E5E7EB",
                    borderRadius: 1.5,
                    height: 70,
                  }}
                >
                  <CardContent
                    sx={{
                      p: 1.5,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0.5,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.3 }}
                      >
                        <CalendarToday
                          sx={{ color: "#6B7280", fontSize: 12 }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          Fecha:
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        sx={{
                          fontSize: "0.6rem",
                          color: "#374151",
                        }}
                      >
                        {serialData.fecha}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ fontSize: "0.65rem" }}
                      >
                        Estado:
                      </Typography>
                      <Chip
                        label={serialData.estado}
                        color={getEstadoColor(serialData.estado) as any}
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.6rem",
                          height: 18,
                          color: "white",
                          "& .MuiChip-label": {
                            px: 0.8,
                          },
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E5E7EB",
                    borderRadius: 1.5,
                    height: 70,
                  }}
                >
                  <CardContent
                    sx={{
                      p: 1.5,
                      textAlign: "center",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <Numbers sx={{ color: "#6B7280", fontSize: 14 }} />
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ fontSize: "0.7rem" }}
                      >
                        No. de meses:
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{
                        fontSize: "0.8rem",
                        color: "#374151",
                        backgroundColor: "white",
                        p: 0.5,
                        borderRadius: 0.5,
                        border: "1px solid #E5E7EB",
                        textAlign: "center",
                      }}
                    >
                      {serialData.meses} meses
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E5E7EB",
                    borderRadius: 1.5,
                    height: 70,
                  }}
                >
                  <CardContent
                    sx={{
                      p: 1.5,
                      textAlign: "center",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <Business sx={{ color: "#6B7280", fontSize: 14 }} />
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ fontSize: "0.7rem" }}
                      >
                        Distribuidor:
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      sx={{
                        fontSize: "0.65rem",
                        color: "#374151",
                        backgroundColor: "white",
                        p: 0.5,
                        borderRadius: 0.5,
                        border: "1px solid #E5E7EB",
                        textAlign: "center",
                        wordBreak: "break-word",
                        lineHeight: 1.1,
                      }}
                    >
                      {serialData?.distribuidor?.name || "Sin distribuidor/empresa"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Cursos */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: "0.85rem" }}
                >
                  Curso(s) Activos: ({serialData.cursos.length})
                </Typography>
                {serialData.cursos.length > 0 && (
                  <Box sx={{ display: "flex", gap: 0.8 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CheckBoxOutlineBlank sx={{ fontSize: 14 }} />}
                      onClick={() => handleToggleChatMasivo(false)}
                      sx={{ fontSize: "0.7rem", py: 0.3 }}
                    >
                      DESACTIVAR CHAT TODOS
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CheckBox sx={{ fontSize: 14 }} />}
                      onClick={() => handleToggleChatMasivo(true)}
                      sx={{
                        fontSize: "0.7rem",
                        py: 0.3,
                        backgroundColor: "#10B981",
                        "&:hover": { backgroundColor: "#059669" },
                      }}
                    >
                      ACTIVAR CHAT TODOS
                    </Button>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: 1.5,
                  p: 1.5,
                  backgroundColor: "#FAFAFA",
                  minHeight: 120,
                  maxHeight: 200,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#F3F4F6",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#D1D5DB",
                    borderRadius: "3px",
                    "&:hover": {
                      backgroundColor: "#9CA3AF",
                    },
                  },
                }}
              >
                {serialData.cursos.map((curso) => (
                  <Box
                    key={curso.code}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                      p: 1,
                      backgroundColor: "white",
                      borderRadius: 1.5,
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Chip
                        label={curso.code}
                        size="small"
                        sx={{
                          backgroundColor: "#10B981",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "0.7rem",
                          height: 24,
                        }}
                      />
                      <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                        {curso.name}
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={curso.chatEnabled}
                            onChange={() => handleToggleChat(curso.code)}
                            size="small"
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "#10B981",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor: "#10B981",
                                },
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Chat
                              sx={{
                                fontSize: 16,
                                color: curso.chatEnabled
                                  ? "#10B981"
                                  : "#9CA3AF",
                              }}
                            />
                            <Typography
                              variant="caption"
                              color={
                                curso.chatEnabled ? "#10B981" : "textSecondary"
                              }
                              sx={{ fontSize: "0.75rem" }}
                            >
                              Chat
                            </Typography>
                          </Box>
                        }
                        labelPlacement="start"
                        sx={{ m: 0 }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  gap: 1.5,
                  alignItems: "center",
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    setSelectedCursos([...serialData.cursos]);
                    setOpenCursosDialog(true);
                  }}
                  sx={{
                    backgroundColor: "#10B981",
                    "&:hover": { backgroundColor: "#059669" },
                    borderRadius: 1.5,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                  }}
                >
                  Gestionar Cursos
                </Button>
                {serialData.cursos.length > 0 && (
                  <Typography
                    variant="caption"
                    color="#10B981"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  >
                    {serialData.cursos.length} curso(s) seleccionado(s) •{" "}
                    {serialData.cursos.filter((c) => c.chatEnabled).length} con
                    chat
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Botones de Acción */}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<Edit sx={{ fontSize: 14 }} />}
                onClick={() => setOpenMesesDialog(true)}
                sx={{
                  backgroundColor: "#6B7280",
                  "&:hover": { backgroundColor: "#4B5563" },
                  borderRadius: 1.5,
                  px: 2,
                  fontSize: "0.75rem",
                }}
              >
                Cambiar # Meses
              </Button>
              <ToggleWithDescription
                value={serialData.estado == "activo" ? true : false}
                onChange={(value) => {
                  setSerialData((prev) => ({
                    ...prev,
                    estado: value ? "activo" : "deshabilitado",
                  }));
                }}
                label="Activar/Desactivar pin"
                description=""
                helperText=""
                color="success"
              />
            </Box>

            {/* Observaciones */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
              >
                <Notes sx={{ color: "#6B7280", fontSize: 16 }} />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: "0.85rem" }}
                >
                  Observaciones:
                </Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                value={serialData.observaciones}
                onChange={(e) =>
                  setSerialData((prev) => ({
                    ...prev,
                    observaciones: e.target.value,
                  }))
                }
                variant="outlined"
                placeholder="Ingrese observaciones sobre este serial..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#B0BEC5",
                    color: "white",
                    fontSize: "0.8rem",
                    "& input::placeholder": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                    "& textarea::placeholder": {
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                  },
                }}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Button
                variant="contained"
                size="medium"
                onClick={handlerUpdate}
                disabled={!isFormValid()}
                sx={{
                  bottom: 7,
                  backgroundColor: "#9CA3AF",
                  color: "white",
                  fontWeight: "bold",
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontSize: "0.8rem",
                  "&:hover": {
                    backgroundColor: "#6B7280",
                  },
                  "&:disabled": {
                    backgroundColor: "#D1D5DB",
                    color: "#9CA3AF",
                  },
                }}
              >
                Actualizar
              </Button>
            </Box>
            {/* Alert informativo */}
            <Alert severity="info" sx={{ borderRadius: 1.5 }}>
              <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                <strong>Información:</strong> Al inactivar el PIN, se
                desactivará el(los) curso(s) para el usuario que tenga asignado
                este serial. El toggle de Chat permite habilitar/deshabilitar el
                acceso al chatbot para cada curso específico.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Dialog para cambiar meses */}
      <Dialog
        open={openMesesDialog}
        onClose={() => setOpenMesesDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1rem" }}>
          Cambiar Número de Meses
        </DialogTitle>
        <DialogContent>
          <NumericInput
            label={"Número de meses:"}
            placeholder={"Ingrese número de meses"}
            sufixText={"meses"}
            helperText="Mínimo 1 mes, máximo 999 meses"
            value={newMeses}
            min={1}
            max={999}
            setter={(value) => {
              setNewMeses(value);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMesesDialog(false)} size="small">
            Cancelar
          </Button>
          <Button
            onClick={handleChangeMeses}
            variant="contained"
            size="small"
            sx={{ backgroundColor: "#10B981" }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para gestionar cursos */}
      <Dialog
        open={openCursosDialog}
        onClose={() => setOpenCursosDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1rem" }}>
          Gestionar Cursos del Serial
        </DialogTitle>
        <DialogContent>
          {/* Cursos Actuales */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{ fontSize: "0.85rem" }}
            >
              Cursos Asignados:
            </Typography>
            {selectedCursos.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.8 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CheckBoxOutlineBlank sx={{ fontSize: 14 }} />}
                  onClick={() => handleToggleChatMasivoModal(false)}
                  sx={{ fontSize: "0.7rem", py: 0.3 }}
                >
                  Desactivar Chat Todos
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CheckBox sx={{ fontSize: 14 }} />}
                  onClick={() => handleToggleChatMasivoModal(true)}
                  sx={{
                    fontSize: "0.7rem",
                    py: 0.3,
                    backgroundColor: "#10B981",
                    "&:hover": { backgroundColor: "#059669" },
                  }}
                >
                  Activar Chat Todos
                </Button>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              mb: 2,
              minHeight: 50,
              maxHeight: 250,
              overflowY: "auto",
              p: 1.5,
              border: "1px solid #E5E7EB",
              borderRadius: 1.5,
              backgroundColor: "#F9FAFB",
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#F3F4F6",
                borderRadius: "2px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#D1D5DB",
                borderRadius: "2px",
                "&:hover": {
                  backgroundColor: "#9CA3AF",
                },
              },
            }}
          >
            {selectedCursos.length === 0 ? (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ alignSelf: "center", fontSize: "0.75rem" }}
              >
                No hay cursos asignados
              </Typography>
            ) : (
              selectedCursos.map((curso) => (
                <Box
                  key={curso.code}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    backgroundColor: "white",
                    borderRadius: 1.5,
                    border: "1px solid #E5E7EB",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                    <Chip
                      label={`${curso.code} - ${curso.name}`}
                      size="small"
                      sx={{
                        backgroundColor: "#10B981",
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "0.7rem",
                      }}
                    />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={curso.chatEnabled}
                          onChange={() => handleToggleChatInModal(curso.code)}
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#10B981",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#10B981",
                              },
                          }}
                        />
                      }
                      label={
                        <Chat
                          sx={{
                            fontSize: 14,
                            color: curso.chatEnabled ? "#10B981" : "#9CA3AF",
                          }}
                        />
                      }
                      sx={{ m: 0 }}
                    />

                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveCurso(curso.code)}
                      sx={{
                        minWidth: "auto",
                        p: 0.5,
                        borderRadius: "50%",
                        "&:hover": {
                          backgroundColor: "rgba(244, 67, 54, 0.1)",
                        },
                      }}
                    >
                      <Close sx={{ fontSize: 14 }} />
                    </Button>
                  </Box>
                </Box>
              ))
            )}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <CourseManagerDialog
            open={openCursosDialog}
            onClose={handleCloseCursosDialog}
            onSave={handleChangeCursos}
            selectedCourses={selectedCursos}
            availableCourses={cursosDisponibles && cursosDisponibles?.length ? cursosDisponibles?.filter(
              (curso) => !selectedCursos.find((c) => c.code === curso.code)
            ) : []}
            onToggleChatAll={handleToggleChatMasivo}
            onToggleChat={handleToggleChatInModal}
            onRemoveCourse={handleRemoveCurso}
            onAddCourse={handleAddCurso}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
